import {
  Actions,
  Button,
  Column,
  Columns,
  IconCopy,
  IconDelete,
  IconEdit,
  IconTick,
  Inline,
  Notice,
  Stack,
  Text,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { NameField } from '../NameField/NameField.tsx';
import { type FormData, FormProvider } from '../NameField/form.tsx';

import { Secret } from './Secret.tsx';

import {
  type GetPartnerTokenPayload,
  type LiveClientPayload,
  type PublicTestClientPayload,
  useApi,
} from '#src/api/index.ts';
import { CopyableText } from '#src/components/CopyableText/CopyableText.tsx';
import { TwoColumnGrid } from '#src/components/TwoColumnGrid/TwoColumnGrid.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';

interface Props {
  credential: LiveClientPayload | PublicTestClientPayload;
  onDeleteRequest: () => unknown;
  onGeneratePartnerToken: (partnerToken: GetPartnerTokenPayload) => void;
  writeAccess: boolean;
}

export const Credential = ({
  credential,
  onDeleteRequest,
  onGeneratePartnerToken,
  writeAccess,
}: Props) => {
  const { permissions } = usePermissions();

  const api = useApi();

  const [isEditing, setIsEditing] = useState(false);

  const [hasClientGrant, setHasClientGrant] = useState(
    credential.hasClientGrant,
  );

  useEffect(
    () => setHasClientGrant(credential.hasClientGrant),
    [credential.hasClientGrant],
  );

  const showToast = useToast();

  const createClientGrant = async () => {
    try {
      await api.credentials.createClientGrant(credential.clientId);

      setHasClientGrant(true);

      showToast({
        key: 'createClientGrant',
        tone: 'positive',
        message: 'Credentials linked.',
      });
    } catch (err) {
      showToast({
        key: 'createClientGrant',
        tone: 'critical',
        message: 'We couldn’t link your credentials.',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const updateClient = async ({ name }: FormData) => {
    try {
      await api.credentials.updateClient(credential.clientId, name.trim());

      setIsEditing(false);
    } catch (err) {
      showToast({
        key: 'updateClient',
        tone: 'critical',
        message: 'We couldn’t save your credential’s name.',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const generatePartnerToken = async () => {
    try {
      const data = await api.credentials.getPartnerToken(credential.clientId);
      onGeneratePartnerToken(data);
    } catch (err) {
      showToast({
        key: 'generatedPartnerToken',
        tone: 'critical',
        message: 'We couldn’t generate a partner token.',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const canGeneratePartnerToken =
    credential.type === 'publicTest' ||
    permissions.includes('query:live-credentials');
  const canDelete = writeAccess;

  return (
    <Columns space="large" collapseBelow="tablet">
      <Column>
        <Stack space="medium">
          <FormProvider initialValues={{ name: credential.name }}>
            {({ handleSubmit }) => (
              <form
                onSubmit={handleSubmit((data) => {
                  updateClient(data);
                })}
              >
                <Inline space="xxsmall">
                  <NameField
                    disabled={!isEditing}
                    id={`update-credential-name-field-${credential.clientId}`}
                    showLabel={false}
                    dataType={credential.type}
                  />
                  {writeAccess ? (
                    <>
                      {isEditing ? (
                        <Button
                          type="submit"
                          variant="transparent"
                          data={{ action: 'submit' }}
                        >
                          <IconTick />
                        </Button>
                      ) : (
                        <Button
                          onClick={(event) => {
                            event.preventDefault();
                            setIsEditing(true);
                          }}
                          type="button"
                          variant="transparent"
                          data={{ action: 'edit' }}
                        >
                          <IconEdit />
                        </Button>
                      )}
                    </>
                  ) : null}
                </Inline>
              </form>
            )}
          </FormProvider>

          <TwoColumnGrid space="medium">
            <Text size="small" weight="medium">
              Client ID
            </Text>

            <CopyableText inlineCode size="small">
              {credential.clientId}
            </CopyableText>

            {credential.clientSecret ? (
              <Secret label="Client secret" secret={credential.clientSecret} />
            ) : null}

            {credential.createdAt ? (
              <>
                <Text size="small" weight="medium">
                  Created
                </Text>

                <Text size="small">
                  {new Date(credential.createdAt).toLocaleString()}
                </Text>
              </>
            ) : null}

            {credential.lastUsedAt ? (
              <>
                <Text size="small" weight="medium">
                  Last used
                </Text>

                <Text size="small">
                  {new Date(credential.lastUsedAt).toLocaleString()}
                </Text>
              </>
            ) : null}
          </TwoColumnGrid>

          {hasClientGrant ? null : (
            <Notice tone="critical">
              <Text>
                These credentials have not been linked to the SEEK API.
                {writeAccess ? (
                  <>
                    {' '}
                    <TextLinkButton onClick={createClientGrant}>
                      Click here
                    </TextLinkButton>{' '}
                    to resolve the issue.
                  </>
                ) : null}
              </Text>
            </Notice>
          )}
        </Stack>
      </Column>

      <Column width="content">
        {canGeneratePartnerToken || canDelete ? (
          <Actions>
            {canGeneratePartnerToken ? (
              <Button
                icon={<IconCopy />}
                size="small"
                variant="soft"
                onClick={generatePartnerToken}
              >
                Generate partner token
              </Button>
            ) : null}

            {canDelete ? (
              <Button
                icon={<IconDelete />}
                onClick={onDeleteRequest}
                size="small"
                variant="soft"
                tone="critical"
              >
                Delete
              </Button>
            ) : null}
          </Actions>
        ) : null}
      </Column>
    </Columns>
  );
};
